import React from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Loader2, Trash2 } from 'lucide-react';

import type { ButtonHTMLAttributes } from 'react';

interface DeleteUserInviteProps {
  orgId: string;
  reqUserId: string | null;
  onDeleted?: () => void;
  buttonLabel?: string;
  buttonProps?: ButtonHTMLAttributes<HTMLButtonElement>;
}

export function DeleteUserInvite({
  orgId,
  reqUserId,
  onDeleted,
  buttonLabel = '',
  buttonProps = {},
}: DeleteUserInviteProps) {
  const [open, setOpen] = React.useState(false);
  const queryClient = useQueryClient();

  const deleteInviteMutation = useMutation({
    mutationFn: async (reqUserId: string) => {
      const response = await fetch(`/api/org/${orgId}/invitedUser/${reqUserId}`, {
        method: 'DELETE',
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to delete invited user');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invitedUsers', orgId] });
      setOpen(false);
      if (onDeleted) onDeleted();
    },
  });

  const handleDeleteInvite = () => {
    if (reqUserId) {
      deleteInviteMutation.mutate(reqUserId);
    }
  };

  return (
    <>
      <Button
        variant="ghost"
        size="icon"
        aria-label="Remove invitation"
        onClick={() => setOpen(true)}
        {...buttonProps}
      >
        {buttonLabel || <Trash2 className="w-4 h-4 text-destructive" />}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove Invitation</DialogTitle>
          </DialogHeader>
          <p>Are you sure you want to remove this user invitation? This cannot be undone.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteInvite}
              disabled={deleteInviteMutation.isPending}
            >
              {deleteInviteMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Removing...
                </>
              ) : (
                'Remove'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

